import React, { useState, useMemo } from "react";
import { useSwipeable } from "react-swipeable";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useResponsive } from "../hooks/useResponsive";
import InteractiveCard from "./InteractiveCard";
import { colors } from "../styles/colors";
import HeroHeading from "./ui/HeroHeading";
import SeoAlternateLinks from "./seo/SeoAlternateLinks";
import { buildAbsoluteUrl, derivePageMeta } from "../utils/seo";
import LazyVideo from "./LazyVideo";
import { useContent } from "../cms/ContentContext";

const GALLERY_IMAGE_DEFINITIONS = [
  {
    id: "aurora-lapland",
    src: "/images/Nya_bilder/IMG_6698.webp",
    titleKey: "gallery.images.winterLandscape",
    fallback: "Northern lights over Lapland",
    category: "landscapes",
    featured: true
  },
  {
    id: "snowmobile-guide",
    src: "/images/Nya_bilder/IMG_4108.webp",
    titleKey: "gallery.images.snowmobileAdventure",
    fallback: "Snowmobile guide on a frozen lake",
    category: "snowmobile",
    featured: true
  },
  {
    id: "cabin-night",
    src: "/images/Nya_bilder/IMG_3493.webp",
    titleKey: "gallery.images.dogSledding",
    fallback: "Cabins glowing in the winter night",
    category: "dogSledding",
    featured: true
  },
  {
    id: "forest-cabin",
    src: "/images/Nya_bilder/IMG_0542.webp",
    titleKey: "gallery.images.snowmobile1",
    fallback: "Log cabin in the winter forest",
    category: "landscapes"
  },
  {
    id: "moonlit-pines",
    src: "/images/Nya_bilder/IMG_0575.webp",
    titleKey: "gallery.images.snowmobile2",
    fallback: "Moonlit pines under a clear sky",
    category: "landscapes"
  },
  {
    id: "aurora-base",
    src: "/images/Nya_bilder/IMG_4527.webp",
    titleKey: "gallery.images.snowmobile3",
    fallback: "Aurora above our snowmobile base",
    category: "snowmobile"
  },
  {
    id: "winter-forest",
    src: "/images/Nya_bilder/IMG_0451.webp",
    titleKey: "gallery.images.landscape1",
    fallback: "Lapland winter forest",
    category: "landscapes"
  },
  {
    id: "team-ready",
    src: "/images/Nya_bilder/IMG_1687.webp",
    titleKey: "gallery.images.landscape2",
    fallback: "Snowmobile team ready for the mountains",
    category: "snowmobile"
  },
  {
    id: "trail-selfie",
    src: "/images/Nya_bilder/coldexperience_born.webp",
    titleKey: "gallery.images.landscape3",
    fallback: "Sunset selfie on the trail",
    category: "snowmobile"
  },
  {
    id: "aurora-tub",
    src: "/images/Nya_bilder/IMG_6181.webp",
    titleKey: "gallery.images.landscape4",
    fallback: "Hot tub beneath the northern lights",
    category: "activities"
  },
  {
    id: "frozen-lake-stop",
    src: "/images/Nya_bilder/IMG_1547.webp",
    titleKey: "gallery.images.activity1",
    fallback: "Snowmobile parked at a frozen lake",
    category: "snowmobile"
  },
  {
    id: "handlebar-view",
    src: "/images/Nya_bilder/IMG_2425.webp",
    titleKey: "gallery.images.family1",
    fallback: "Trail view from the snowmobile handlebars",
    category: "snowmobile"
  },
  {
    id: "sunrise-homestead",
    src: "/images/Nya_bilder/IMG_4436.webp",
    titleKey: "gallery.images.memories1",
    fallback: "Sunrise over our snowy homestead",
    category: "landscapes"
  },
  {
    id: "summit-celebration",
    src: "/images/Nya_bilder/IMG_7834.webp",
    titleKey: "gallery.images.authentic1",
    fallback: "Guests celebrating on the summit",
    category: "activities"
  },
  {
    id: "outdoor-hot-tub",
    src: "/images/Nya_bilder/IMG_1634.webp",
    titleKey: "gallery.images.scenic1",
    fallback: "Relaxing in the outdoor hot tub",
    category: "activities"
  },
  {
    id: "lakeside-aurora",
    src: "/images/Nya_bilder/IMG_3700.webp",
    titleKey: "gallery.images.scenic2",
    fallback: "Aurora above the lakeside cabin",
    category: "landscapes"
  },
  {
    id: "campfire-hut",
    src: "/images/Nya_bilder/IMG_4545.webp",
    titleKey: "gallery.images.scenic3",
    fallback: "Campfire outside the timber hut",
    category: "activities"
  },
  {
    id: "hosts-grilling",
    src: "/images/Nya_bilder/IMG_5562.webp",
    titleKey: "gallery.images.scenic4",
    fallback: "Hosts grilling inside the kota",
    category: "activities"
  },
  {
    id: "winter-cabin-sunrise",
    src: "/images/Nya_bilder/IMG_3838.webp",
    titleKey: "gallery.images.winter1",
    fallback: "Winter sunrise over the cabin",
    category: "landscapes"
  },
  {
    id: "evening-campfire",
    src: "/images/Nya_bilder/IMG_3860.webp",
    titleKey: "gallery.images.winter2",
  fallback: "Aurora dancing across the polar sky",
    category: "activities"
  },
  {
    id: "family-winter-sun",
    src: "/images/Nya_bilder/IMG_2963.webp",
    titleKey: "gallery.images.adventure1",
  fallback: "Family break by the wilderness shelter",
    category: "activities"
  },
  // ...removed 'Firelit cabin in the forest' image...
  {
    id: "family-trail-moment",
    src: "/images/Nya_bilder/family_1.webp",
    titleKey: "gallery.images.familyGathering",
    fallback: "Family joy on the winter trail",
    category: "activities"
  },
  {
    id: "family-snow-laughter",
    src: "/images/Nya_bilder/family_2.webp",
    titleKey: "gallery.images.familyJoy",
    fallback: "Laughter in the Lapland snow",
    category: "activities"
  },
  // ...removed 'Love for Arctic adventures' image...
  {
    id: "moonlit-cabin",
    src: "/images/Nya_bilder/IMG_0554 (1).webp",
    titleKey: "gallery.images.moonlitCabin",
    fallback: "Moonlit cabin in deep winter",
    category: "landscapes"
  },
  {
    id: "fireplace-feast",
    src: "/images/Nya_bilder/IMG_1579.webp",
    titleKey: "gallery.images.fireplaceFeast",
    fallback: "Lapland flavours grilled over open fire",
    category: "activities"
  },
  {
    id: "aurora-dance",
    src: "/images/Nya_bilder/IMG_1904.webp",
    titleKey: "gallery.images.auroraDance",
    fallback: "Aurora dancing across the polar sky",
    category: "landscapes"
  },
  // ...removed 'Sunset snowmobile selfie in Lapland' image...
  {
    id: "fireside-shelter",
    src: "/images/Nya_bilder/IMG_4082.webp",
    titleKey: "gallery.images.firesideShelter",
  fallback: "Firelit cabin in the snowy forest",
    category: "activities"
  },
  {
    id: "star-camp",
    src: "/images/Nya_bilder/IMG_4596 (1).webp",
    titleKey: "gallery.images.starCamp",
    fallback: "Starlit night at our mountain shelter",
    category: "landscapes"
  },
  {
    id: "campfire-cooking",
    src: "/images/Nya_bilder/julias_matresa.webp",
    titleKey: "gallery.images.campfireCooking",
    fallback: "Campfire cooking with local ingredients",
    category: "activities"
  },
  {
    id: "aurora-pines",
    src: "/images/Nya_bilder/IMG_6702.webp",
    titleKey: "gallery.images.auroraPines",
    fallback: "Northern lights above the pine forest",
    category: "landscapes"
  },
  {
    id: "fireside-lake",
    src: "/images/Nya_bilder/IMG_7476 (1).webp",
    titleKey: "gallery.images.firesideLake",
    fallback: "Snowmobile camp by the frozen lake",
    category: "snowmobile"
  },
  // ...removed aurora-motion film...
];

const CATEGORY_DEFINITIONS = [
  { id: "all", labelKey: "gallery.categories.all", fallback: "All" },
  { id: "featured", labelKey: "gallery.categories.featured", fallback: "Featured" },
  { id: "snowmobile", labelKey: "gallery.categories.snowmobile", fallback: "Snowmobile" },
  { id: "dogSledding", labelKey: "gallery.categories.dogSledding", fallback: "Dog sledding" },
  { id: "landscapes", labelKey: "gallery.categories.landscapes", fallback: "Landscapes" },
  { id: "activities", labelKey: "gallery.categories.activities", fallback: "Activities" }
];

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const { isMobile } = useResponsive();
  const { t, i18n } = useTranslation();
  const { getMedia } = useContent();
  const meta = derivePageMeta({
    t,
    i18n,
    pageKey: "gallery",
    routeKey: "gallery",
    defaults: {
      ogImage: "/og/gallery.jpg",
    },
  });
  const ogImageUrl = buildAbsoluteUrl(meta.siteUrl, meta.ogImage);
  const twitterImageUrl = buildAbsoluteUrl(meta.siteUrl, meta.twitterImage);
  const heroSpacing = isMobile ? "px-4 pt-24 pb-16" : "px-4 sm:px-6 lg:px-8 pt-32 pb-24";
  const heroTitle = t("gallery.heroTitle", "Vårt Galleri");
  const heroTitlePrimary = t("gallery.heroTitlePrimary", "");
  const heroTitleAccent = t("gallery.heroTitleAccent", "");
  const heroHeadingTitle = !heroTitlePrimary && !heroTitleAccent ? heroTitle : undefined;

  const galleryImages = useMemo(
    () =>
      GALLERY_IMAGE_DEFINITIONS.map((image) => ({
        ...image,
        src: getMedia(`gallery.image.${image.id}`, image.src),
        title: t(image.titleKey, image.fallback)
      })),
    [t, getMedia]
  );

  // Categories for filtering
  const categories = useMemo(
    () =>
      CATEGORY_DEFINITIONS.map(({ id, labelKey, fallback }) => ({
        id,
        label: t(labelKey, fallback)
      })),
    [t]
  );

  // Filter images based on active category
  const filteredImages = useMemo(() => {
    if (activeCategory === 'all') return galleryImages;
    if (activeCategory === 'featured') {
      return galleryImages.filter(img => img.featured);
    }
    return galleryImages.filter(img => img.category === activeCategory);
  }, [galleryImages, activeCategory]);

  const openModal = (image) => {
    setSelectedImage(image);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  const navigateImage = (direction) => {
    if (!selectedImage || filteredImages.length === 0) return;

    const currentIndex = filteredImages.findIndex((img) => img.id === selectedImage.id);
    if (currentIndex === -1) return;

    if (direction === 'next') {
      const nextIndex = (currentIndex + 1) % filteredImages.length;
      setSelectedImage(filteredImages[nextIndex]);
      return;
    }

    const prevIndex = currentIndex === 0 ? filteredImages.length - 1 : currentIndex - 1;
    setSelectedImage(filteredImages[prevIndex]);
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => navigateImage('next'),
    onSwipedRight: () => navigateImage('prev'),
    trackMouse: true,
    preventScrollOnSwipe: true
  });

  const navIconClass = isMobile ? 'h-5 w-5' : 'h-6 w-6';

  return (
    <div className="min-h-screen" style={{ background: colors.gradients.heroSolid }}>
      <Helmet>
        {meta.title && <title>{meta.title}</title>}
        {meta.description && <meta name="description" content={meta.description} />}
        {meta.keywords && <meta name="keywords" content={meta.keywords} />}
        <link rel="canonical" href={meta.canonicalUrl} />
        <meta property="og:title" content={meta.ogTitle} />
        <meta property="og:description" content={meta.ogDescription} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:url" content={meta.canonicalUrl} />
        <meta name="twitter:image" content={twitterImageUrl} />
        {meta.jsonLd && (
          <script type="application/ld+json">{JSON.stringify(meta.jsonLd)}</script>
        )}
      </Helmet>
      <SeoAlternateLinks
        routeKey="gallery"
        canonicalPath={meta.canonicalPath}
        siteUrl={meta.siteUrl}
      />

      <section
        data-hero="true"
        data-cms-section="gallery:hero"
        data-cms-page="gallery"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{ background: colors.gradients.hero }}
      >
        <div className="absolute inset-0 overflow-hidden">
          <LazyVideo
            src={getMedia('gallery.hero.videoSrc', '/optimized_videos/galleri-opt.mp4')}
            poster={getMedia('gallery.hero.poster', '/images/Nya_bilder/IMG_6702.webp')}
            autoPlay
            loop
            muted
            playsInline
            preload={isMobile ? "none" : "metadata"}
            className="absolute inset-0"
            videoClassName="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0" style={{ background: colors.gradients.hero }}></div>
        </div>
        <div className={`relative z-10 w-full ${heroSpacing}`}>
          <div
            className={`${isMobile ? 'max-w-sm' : 'max-w-4xl'} mx-auto text-center`}
            style={{ color: colors.text.primary }}
          >
            <HeroHeading
              primary={heroTitlePrimary || undefined}
              accent={heroTitleAccent || undefined}
              title={heroHeadingTitle}
              stacked
              className={`mb-6 ${isMobile ? 'text-4xl' : 'text-5xl md:text-6xl'}`}
            />
            <p
              className={`${isMobile ? 'text-lg' : 'text-xl md:text-2xl'} mb-8 leading-relaxed`}
            >
              {t("gallery.heroSubtitle", "Upptäck magin i Lapplands vinterlandskap genom våra fantastiska bilder")}
            </p>
          </div>
        </div>
      </section>

      <main data-cms-section="gallery:grid" className={`min-h-screen ${
        isMobile ? 'py-12' : 'py-20'
      }`} style={{ background: colors.gradients.heroSolid }}>
        <div className={`mx-auto ${
          isMobile ? 'max-w-sm px-4' : 'max-w-7xl px-4 sm:px-6 lg:px-8'
        }`}>
          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-4 mb-10">
            {categories.map(({ id, label }) => (
              <InteractiveCard
                key={id}
                variant="glass"
                className={`px-5 py-2 cursor-pointer transition-transform duration-300 ${
                  activeCategory === id ? 'scale-105 shadow-lg' : 'hover:scale-105'
                }`}
                onClick={() => setActiveCategory(id)}
              >
                <span className="font-semibold" style={{ color: colors.text.primary }}>
                  {label}
                </span>
              </InteractiveCard>
            ))}
          </div>

          {filteredImages.length === 0 ? (
            <div className="py-16 text-center">
              <p className="text-lg" style={{ color: colors.text.secondary }}>
                {t("gallery.noImages", "No images found for this category.")}
              </p>
            </div>
          ) : (
            <div className={`grid gap-6 ${
              isMobile ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
            }`}>
              {filteredImages.map((image) => (
                <InteractiveCard
                  key={image.id}
                  variant="glass"
                  className="relative overflow-hidden cursor-pointer group"
                  onClick={() => openModal(image)}
                >
                  <div className="relative">
                    <img
                      src={image.src}
                      alt={image.title}
                      className={`w-full object-cover transition-transform duration-500 group-hover:scale-110 ${
                        isMobile ? 'h-64' : 'h-72'
                      }`}
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className={`absolute text-white ${
                        isMobile ? 'bottom-3 left-3' : 'bottom-4 left-4'
                      }`}>
                        <h3 className={`font-bold drop-shadow-lg ${
                          isMobile ? 'text-base' : 'text-lg'
                        }`}>{image.title}</h3>
                      </div>
                    </div>
                  </div>
                </InteractiveCard>
              ))}
            </div>
          )}

        </div>
      </main>

      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-2">
          <div
            className={[
              "relative flex w-full h-full flex-col items-center justify-center",
              isMobile ? "max-w-full" : "max-w-7xl"
            ].join(' ')}
            {...(isMobile ? swipeHandlers : {})}
          >
            <button
              type="button"
              onClick={closeModal}
              className={[
                "absolute z-20 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 text-white transition-colors duration-200",
                isMobile ? "top-3 right-3 p-2" : "top-6 right-6 p-3"
              ].join(' ')}
              aria-label={t('gallery.closeModal', 'Close gallery')}
            >
              <X className={isMobile ? 'h-5 w-5' : 'h-6 w-6'} />
            </button>

            {filteredImages.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => navigateImage('prev')}
                  className={[
                    "absolute top-1/2 -translate-y-1/2 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 text-white transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black",
                    isMobile ? "left-2 p-2" : "left-4 p-3"
                  ].join(' ')}
                  aria-label={t('gallery.prevImage', 'Previous image')}
                >
                  <ChevronLeft className={navIconClass} />
                </button>
                <button
                  type="button"
                  onClick={() => navigateImage('next')}
                  className={[
                    "absolute top-1/2 -translate-y-1/2 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 text-white transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black",
                    isMobile ? "right-2 p-2" : "right-4 p-3"
                  ].join(' ')}
                  aria-label={t('gallery.nextImage', 'Next image')}
                >
                  <ChevronRight className={navIconClass} />
                </button>
              </>
            )}

            <img
              src={selectedImage.src}
              alt={selectedImage.title}
              loading="lazy"
              decoding="async"
              className={[
                "w-full h-auto object-contain rounded-lg",
                isMobile ? "max-h-[70vh]" : "max-h-[80vh]"
              ].join(' ')}
              style={isMobile ? { boxShadow: '0 2px 16px rgba(0,0,0,0.5)' } : undefined}
            />

            <div className="w-full flex flex-col items-center mt-3">
              <div
                className={[
                  "bg-black bg-opacity-40 rounded-lg mx-auto",
                  isMobile ? "max-w-full px-3 py-2" : "max-w-xl px-4 py-3"
                ].join(' ')}
              >
                <style>{`
                  .gallery-modal-title {
                    font-family: 'Montserrat', sans-serif;
                    font-weight: 600;
                    color: #F5E9D7;
                    letter-spacing: 0.04em;
                    text-shadow: 0 2px 8px rgba(0,0,0,0.18);
                    margin-bottom: 0.5rem;
                  }
                `}</style>
                <h3
                  className={[
                    "gallery-modal-title text-center",
                    isMobile ? "text-lg" : "text-2xl"
                  ].join(' ')}
                >
                  {selectedImage.title}
                </h3>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
